/**
 * PROC-01 Verbatim n-gram fidelity (INV-PACK-01, ENGINE-SPEC §3.3, §4; change C-D6-PROC01).
 *
 * The pack counted distinct 6-gram types recurring 3+ times per 10k tokens (a type-count that
 * grows with length, D6). Here the pack's hypothesis ("a model with the phrase in context
 * reproduces it at perfect fidelity; a human paraphrases and degrades it") is measured as a
 * length-free ratio over *distinctive* positions (6-gram windows with ≥ 2 words outside
 * FRAME_STOP), censored at the document start:
 *
 *     F = R6(ℓ) / R3(ℓ),   ℓ = min(20000, largest estimable lag)
 *
 * R6 is the Kaplan–Meier probability that the window recurred verbatim within ℓ words and R3 the
 * same for its leading 3-gram, i.e. the share of "motif-echoing" positions that echo all six words.
 *
 * Per-paragraph series: e = positions whose leading 3-gram recurred within L_ref, k = those whose
 * whole 6-gram recurred, so ΣK/ΣE ≈ F is on the display scale and the hotspot tests compare like
 * with like (the seed-derived plan used all distinctive positions as e; that would put K/E at R6,
 * a different quantity from the displayed F).
 */
import { estimableLag, hashWindow, recurrenceAt, scanRecurrence } from '../segments/recurrence.ts';
import {
	abstainResult,
	derivePrior,
	languageGate,
	overdispersion,
	scoredResult,
} from './framework.ts';
import {
	SMALL,
	addRunSpans,
	frameStopFlags,
	makeEvidence,
	minAtRiskFor,
	proseWordSeq,
} from './helpers.ts';
import type { ChannelDef, ChannelImpl, ChannelResult, Ramp, RunContext } from './types.ts';

// GUESS (ENGINE-SPEC §4): C2 fits this ramp on a human corpus.
const RAMP: Ramp = [
	[0, 0],
	[0.1, 0],
	[0.4, 100],
];
const L_REF = 20000;
const GRAM = 6;
const LEAD = 3;
/** Prior pseudo-count of 3-gram recurrences behind the conditional proportion F (data-weight only). */
const M_EVENTS = 20;

export const proc01Def: ChannelDef = {
	id: 'PROC-01',
	name: 'Verbatim n-gram fidelity',
	family: 'REUSE',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'recurrence',
	features: [
		{ id: 'fidelity', label: 'Fidelity F = R6(L_ref) / R3(L_ref)' },
		{ id: 'r6', label: 'Distinctive 6-gram recurrence R6(L_ref)' },
		{ id: 'r3', label: 'Leading 3-gram recurrence R3(L_ref)' },
	],
	minExposure: 1500,
	exposureUnit: 'words',
	lang: 'agnostic',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'fidelity ratio R6/R3',
	prior: derivePrior(RAMP, 1, 'words', 1.5),
	weight: 1.5,
	nd: 0.15,
	changes: ['C-D6-PROC01'],
};

function run(ctx: RunContext): ChannelResult {
	const def = proc01Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const seq = proseWordSeq(doc);
	const n = seq.count;
	if (n < def.minExposure)
		return abstainResult(def, `insufficient text: ${n} words < ${def.minExposure}`, notes);

	const stop = frameStopFlags(doc);
	const W = n - GRAM + 1;
	const keys6 = new Float64Array(n);
	const keys3 = new Float64Array(n);
	let distinctive = 0;
	for (let i = 0; i < W; i++) {
		let anchors = 0;
		for (let j = 0; j < GRAM; j++) if (!stop[seq.id[i + j] as number]) anchors++;
		if (anchors < 2) continue;
		keys6[i] = hashWindow(seq.id, i, GRAM);
		keys3[i] = hashWindow(seq.id, i, LEAD);
		distinctive++;
	}

	const scan6 = scanRecurrence(keys6, null, L_REF);
	const curve6 = scan6.curve;
	const dist6 = scan6.dist;
	const scan3 = scanRecurrence(keys3, null, L_REF);
	const curve3 = scan3.curve;
	const dist3 = scan3.dist;

	// both curves must be estimable at ℓ (the 3-gram curve loses positions at risk sooner)
	const atRisk = minAtRiskFor(curve6.n);
	const lag = Math.min(L_REF, estimableLag(curve6, atRisk), estimableLag(curve3, atRisk));
	const shortLookback = lag < L_REF;
	if (shortLookback) notes.push('shortLookback');
	const kappaFactor = Math.min(1, lag / L_REF);
	const R6 = recurrenceAt(curve6, lag);
	const R3 = recurrenceAt(curve3, lag);
	let F = 0;
	if (R3 > 0) F = Math.min(1, R6 / R3);
	else notes.push('no leading 3-gram recurrence: fidelity set to 0');

	let n3 = 0;
	let n6 = 0;
	for (let t = 1; t <= lag; t++) {
		n3 += curve3.events[t] as number;
		n6 += curve6.events[t] as number;
	}

	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	for (let i = 0; i < W; i++) {
		if ((dist3[i] as number) === 0) continue;
		const p = seq.para[i + GRAM - 1] as number;
		e[p] = (e[p] as number) + 1;
		if ((dist6[i] as number) > 0) k[p] = (k[p] as number) + 1;
	}
	let K = 0;
	let E = 0;
	for (let p = 0; p < P; p++) {
		K += k[p] as number;
		E += e[p] as number;
	}
	const phi = overdispersion(k, e, K, E, def.prior.phi0, true);
	const pTilde = (n6 + 0.5) / (n3 + 1);
	const se = Math.sqrt((pTilde * (1 - pTilde)) / (n3 + 1)) * Math.sqrt(phi);
	const ci90: [number, number] = [Math.max(0, F - 1.645 * se), Math.min(1, F + 1.645 * se)];
	const kappa = (distinctive / (distinctive + def.prior.m)) * kappaFactor * (n3 / (n3 + M_EVENTS));

	// evidence: merged runs of verbatim-recurring distinctive 6-gram positions
	const ev = makeEvidence();
	addRunSpans(
		doc,
		ev,
		seq,
		(i) => (dist6[i] as number) > 0,
		W,
		GRAM,
		() => 1
	);

	// whole-document refrain list (pack format, overlaps collapsed like the seed); events only
	const grams = new Map<number, { pos: number; first: number; count: number }>();
	for (let i = 0; i < W; i++) {
		const d = dist6[i] as number;
		if (d === 0) continue;
		const key = keys6[i] as number;
		const g = grams.get(key);
		if (g) g.count++;
		else grams.set(key, { pos: i, first: i - d, count: 2 });
	}
	const repeats = [...grams.values()].filter((g) => g.count >= 3).sort((a, b) => a.first - b.first);
	const collapsed: Array<{ pos: number; count: number }> = [];
	let lastEnd = -1;
	let lastCount = 0;
	for (const g of repeats) {
		if (g.first <= lastEnd && g.count === lastCount) {
			lastEnd = g.first + GRAM;
			continue;
		}
		collapsed.push({ pos: g.pos, count: g.count });
		lastEnd = g.first + GRAM;
		lastCount = g.count;
	}
	const summary = collapsed
		.sort((a, b) => b.count - a.count || a.pos - b.pos)
		.slice(0, 8)
		.map((g) => {
			const words: string[] = [];
			for (let j = 0; j < GRAM; j++) words.push(doc.tokenText(seq.tok[g.pos + j] as number));
			return `${g.count}x "${words.join(' ')}"`;
		});

	return scoredResult(def, {
		K,
		E,
		raw: F,
		estimate: F,
		ci90,
		deviation: (def.direction * (F - def.prior.mu)) / Math.max(se, SMALL),
		phi,
		kappa,
		perParagraph: { k, e },
		evidence: ev.build(),
		notes,
		features: { fidelity: F, r6: R6, r3: R3, lag, echoEvents3: n3, echoEvents6: n6, distinctive },
		summary,
	});
}

export const proc01: ChannelImpl = { def: proc01Def, phase: 'event', run };
